import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router'
import { useContext } from 'react'

import ProviderManagementContext from '../ProviderManagementContext'
import { selectNettingUpdaterStoreData } from './nettingUpdaterSelectors'
import {
  setOrganizationId,
  setFile,
  clearFile,
  reset,
} from './nettingUpdaterSlice'
import { nettingUpdaterActions } from './nettingUpdaterSagas'

const useNettingUpdater = () => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useTranslation('organizationManagement')
  const navigate = useNavigate()
  const { providerUuid } = useParams()
  const fileRef = useRef(null)

  const providerContext = useContext(ProviderManagementContext)
  const providerState = providerContext.providerState
  const providerName = providerState.selectedProvider?.name || ''
  const providerId = providerState.selectedProvider?.id || null
  const acceptedExtensions = providerState.selectedProvider?.netting_accepted_extensions || []

  const {
    fileName,
    isSubmitting,
    resultError,
    resultMessage,
  } = useSelector(selectNettingUpdaterStoreData)

  useEffect(() => {
    dispatch(reset())
    if (providerId) {
      dispatch(setOrganizationId(providerId))
    }
  }, [providerId])

  const isFormValid = useMemo(
    () => Boolean(providerId && fileName),
    [providerId, fileName],
  )

  const onFileChange = e => {
    const file = e?.target?.files?.[0] || null
    fileRef.current = file
    dispatch(setFile(file))
  }

  const onFileClear = () => {
    fileRef.current = null
    dispatch(clearFile())
  }

  const onSubmit = () => {
    dispatch({
      type: nettingUpdaterActions.SUBMIT,
      enqueue,
      t,
      file: fileRef.current,
    })
  }

  const onGoBack = () => {
    navigate(-1)
  }

  const acceptedExtensionsString = useMemo(
    () => acceptedExtensions.map(ext => `.${ext}`).join(','),
    [acceptedExtensions],
  )

  return {
    form: {
      providerName,
      providerUuid,
      providerId,
      fileName,
      isSubmitting,
      isValid: isFormValid,
      acceptedExtensions: acceptedExtensionsString,

      onFileChange,
      onFileClear,
      onSubmit,
      onGoBack,
    },
    result: {
      error: resultError,
      message: resultMessage,
    },
    t,
  }
}

export default useNettingUpdater
